import { useCallback, useEffect, useState, useRef } from 'react';
import useFetch, { CachePolicies } from 'use-http';
import { useUnit } from 'effector-react';
import { $user } from 'models/user';
import { $store } from 'models/store';
import { GroupsEnum } from 'types/users';
import { TableFilter } from 'components/UI/Table';
import { convertUserFilter, extractManagers } from 'utils/helpers';
import { ISelectOptions } from 'types/utils';
import { debounce } from 'lodash';

export const useStoresAndUsers = (filter: TableFilter) => {
	const { get } = useFetch({ cachePolicy: CachePolicies.NO_CACHE });
	const user = useUnit($user);
	const store = useUnit($store);

	const [userList, setUserList] = useState([]);
	const [isLoadingUsers, setIsLoadingUsers] = useState(false);
	const [storeIdOptions, setStoreIdOptions] = useState<Array<{ value: string; label: string }>>([]);
	const [regionalManagersOptions, setRegionalManagersOptions] = useState<{ label: string; value: string }[]>([]);
	const [aoManagersOptions, setAoManagersOptions] = useState<{ label: string; value: string }[]>([]);

	const isFilterLocked = !user?.groups?.some(
		group => group === GroupsEnum.ADMIN || group === GroupsEnum.FULL_ACCESS || group.startsWith('/rdaccess/rdaccess.'),
	);

	const receiveUsers = useCallback(
		async (storeId?: string) => {
			setIsLoadingUsers(true);
			try {
				const params = new URLSearchParams();
				
				if (filter.fromDate) {
					params.set('fromDate', filter.fromDate.toString());
				}
				if (filter.toDate) {
					params.set('toDate', filter.toDate.toString());
				}
				if (storeId) {
					params.set('storeId', storeId);
				}
				// Не передаем type - список пользователей должен показывать всех пользователей магазина за период
				// независимо от типа нарушения, чтобы не сбрасывался выбранный userId при смене типа

				const resultUserFios = await get(`api/breach/getUsersFio?${params.toString()}`);
				if (resultUserFios) {
					setUserList(resultUserFios);
				} else {
					setUserList([]);
				}
			} catch (error) {
				console.error('Error receiving users:', error);
				setUserList([]);
			} finally {
				setIsLoadingUsers(false);
			}
		},
		[filter.fromDate, filter.toDate, get],
	);

	// Создаем ref для debounced функции
	const debouncedReceiveUsersRef = useRef(debounce(receiveUsers, 300));
	
	// Обновляем debounced функцию при изменении receiveUsers
	useEffect(() => {
		debouncedReceiveUsersRef.current?.cancel();
		debouncedReceiveUsersRef.current = debounce(receiveUsers, 300);
		return () => {
			debouncedReceiveUsersRef.current?.cancel();
		};
	}, [receiveUsers]);

	const debouncedReceiveUsers = useCallback((storeId?: string) => {
		debouncedReceiveUsersRef.current(storeId);
	}, []);

	const init = useCallback(async () => {
		try {
			if (isFilterLocked && store?.id) {
				const resultStore = await get(`api/stores/${store.id}`);

				if (resultStore) {
					const formattedStores = [
						{
							value: resultStore.id,
							label: resultStore.number,
						},
					];

					await receiveUsers(store.id);
					setStoreIdOptions(formattedStores);
				}
			} else {
				const resultStores = await get(`api/stores`);
				if (resultStores?.rows) {
					const formattedStores = resultStores.rows.map((store: { id: any; number: any }) => ({
						value: store.id,
						label: store.number,
					}));

					const { regionalManagers, aoManagers } = extractManagers(resultStores.rows);

					setRegionalManagersOptions(regionalManagers);
					setAoManagersOptions(aoManagers);
					setStoreIdOptions(formattedStores);
				}
			}
		} catch (error) {
			console.error('Error initializing stores and users:', error);
		}
	}, [isFilterLocked, store, get, receiveUsers]);

	useEffect(() => {
		init();
	}, [init]);

	useEffect(() => {
		if (filter.storeId) {
			debouncedReceiveUsers(filter.storeId);
		}
	}, [filter.storeId, debouncedReceiveUsers]);

	useEffect(() => {
		if (storeIdOptions.length === 0) return;
		if (storeIdOptions.length === 1) {
			debouncedReceiveUsers(storeIdOptions[0].value);
		} else if (!filter.storeId) {
			debouncedReceiveUsers();
		}
	}, [filter.fromDate, filter.toDate, storeIdOptions, debouncedReceiveUsers, filter.storeId]);

	const loadUser = useCallback(
		async (inputValue: string): Promise<ISelectOptions[]> => {
			try {
				let data = [];
				const storeId = filter.storeId || (storeIdOptions.length === 1 ? storeIdOptions[0].value : '');
				
				const params = new URLSearchParams();
				if (inputValue) {
					params.set('fio', inputValue);
				}
				if (storeId) {
					params.set('storeId', String(storeId));
				}
				if (filter.fromDate) {
					params.set('fromDate', filter.fromDate.toString());
				}
				if (filter.toDate) {
					params.set('toDate', filter.toDate.toString());
				}
				// Не передаем type - показываем всех пользователей магазина за период

				if (inputValue || storeId) {
					data = await get(`/api/breach/getUsersFio?${params.toString()}`);
				}
				return convertUserFilter(data);
			} catch (error) {
				console.error('Error loading users:', error);
				return [];
			}
		},
		[filter.storeId, filter.fromDate, filter.toDate, storeIdOptions, get],
	);

	return {
		userList,
		isLoadingUsers,
		storeIdOptions,
		regionalManagersOptions,
		aoManagersOptions,
		isFilterLocked,
		receiveUsers,
		loadUser,
		init,
	};
};
